import { Component, OnInit, OnDestroy } from "@angular/core";
import { DroneReport } from '../../core/data-models/drone-report.model';
import { DroneReportCompletionEvent } from '../../core/response-models/drone-report-completion-event.model';
import { DroneReportFailedEvent } from '../../core/response-models/drone-report-failed-event.model';
import { DroneReportFilters } from '../../core/request-models/drone-report-filters.model';
import { DroneReportProgressEvent } from '../../core/response-models/drone-report-progress-event.model';
import { DroneReportStatus } from '../../core/data-models/drone-report-status.model';
import { DroneReportWithJobs } from '../../core/response-models/drone-report-with-jobs.model';
import { ReportDrone } from '../../core/response-models/report-drone.model';
import { formatReportDate, formatReportDateRange, formatReportTimestamp, getOrganizationName, getReportStatusClass, getReportStatusIcon, getReportStatusLabel } from "../../shared/utils/report.utils";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { OrganizationOption } from 'src/app/core/response-models/organization-option.model';
import { DroneService } from "src/app/core/data-services/drone.service";
import { DroneReportService } from "src/app/core/data-services/drone-report.service";
import { withChanges } from "src/app/core/utils/model-utils";
import { firstValueFrom } from "rxjs";
import { WebSocketService } from "src/app/web-socket-handlers/web-socket.service";
import { openExternal } from '../../core/utils/external-link-utils';


@Component({
  selector: 'app-drone-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './drone-reports.component.html',
  styleUrl: './drone-reports.component.scss'
})
export class DroneReportsComponent implements OnInit, OnDestroy {
    reports: DroneReport[] = [];
    organizations: OrganizationOption[] = [];
    drones: ReportDrone[] = [];
    selectedDrone: ReportDrone | null = null;
    selectedReport: DroneReportWithJobs | null = null;

    // states
    loading = false;
    errorMessage = '';
    successMessage = '';
    dronesLoading = true;
    private successMessageTimeout: ReturnType<typeof setTimeout> | null = null;

    // drone loading
    private adnDronesReady: Promise<void> | null = null;
    private orgDroneRequests = new Map<string, Promise<void>>();

    //generating report
    reportStartDate = '';
    reportEndDate = '';
    reportGenerating = false;
    generateSectionExpanded = false;
    droneSearch = '';
    filteredDrones: ReportDrone[] = [];

    // delete modal
    reportToDelete: DroneReport | null = null;

    // regenerate modal
    reportToRegenerate: DroneReport | null = null;
    actionLoading = false;

    // Filters
    searchQuery = '';
    filterStatus: 'all' | DroneReportStatus = 'all';
    filterOrganization = '';
    filterDrone = '';
    filterStartDate = '';
    filterEndDate = '';

    // Status options for filter
    statusOptions: { value: string; label: string }[] = [
        { value: 'all', label: 'All Statuses' },
        { value: 'generating', label: 'Generating' },
        { value: 'ready', label: 'Ready' },
        { value: 'failed', label: 'Failed' },
        { value: 'draft', label: 'Draft' }
    ];

    // Pagination
    currentPage = 1;
    pageSize = 20;
    totalReports = 0;
    totalPages = 0;

    // polling
    private pollingInterval: ReturnType<typeof setInterval> | null = null;
    private readonly POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds when generating

    constructor(
        private droneReportService: DroneReportService,
        private droneService: DroneService,
        private webSocketService: WebSocketService
    ) {}

    // ======================
    // LIFECYCLE METHODS
    // ======================

    ngOnInit(): void {
        this.webSocketService.registerEventHandler('drone-report:progress', this.onReportProgress)
        this.webSocketService.registerEventHandler('drone-report:complete', this.onReportCompletion)
        this.webSocketService.registerEventHandler('drone-report:failed', this.onReportFailure)

        this.adnDronesReady = this.loadAdnDrones();
        this.loadReports();
        this.loadOrganizations()
            .then(() => Promise.all(this.organizations.map(o => this.loadOrgDrones(o.id))))
            .finally(() => this.dronesLoading = false);
    }

    ngOnDestroy(): void {
       this.webSocketService.unregisterEventHandler('drone-report:progress', this.onReportProgress);
       this.webSocketService.unregisterEventHandler('drone-report:complete', this.onReportCompletion);
       this.webSocketService.unregisterEventHandler('drone-report:failed', this.onReportFailure);
       this.stopPolling();
       if (this.successMessageTimeout) clearTimeout(this.successMessageTimeout);
    }

    onSearch(): void {
        this.currentPage = 1;
        this.loadReports();
    }

    onFilterChange(): void {
        this.currentPage = 1;
        this.loadReports();
    }

    toggleGenerateSection(): void {
        this.generateSectionExpanded = !this.generateSectionExpanded;
    }

    isDroneSelected(droneId: string): boolean {
        return this.selectedDrone?.id === droneId;
    }

    isDateRangeValid(): boolean {
        if (!this.reportStartDate || !this.reportEndDate) {
        return true;
        }
        const start = new Date(this.reportStartDate);
        const end = new Date(this.reportEndDate);
        return end >= start;
    }

    canSubmitReport(): boolean {
        return this.selectedDrone !== null && this.isDateRangeValid();
    }

    getDroneReportConfirmationText(): string {
        return `Generate Drone report for ${this.selectedDrone?.name}`;
    }

    confirmDroneReportGeneration(): void {
        if (!this.canSubmitReport()) return;
        this.openModal('droneReportConfirmModal');
    }

    cancelDroneReportConfirmation(): void {
        this.closeModal('droneReportConfirmModal');
    }

    applyDroneFilter(): void {
        const searchLower = this.droneSearch.toLowerCase().trim();
        const generatableDrones = this.drones.filter(drone => drone.canGenerateReport);
        
        if (!searchLower) {
            this.filteredDrones = [...generatableDrones];
        } else {
            this.filteredDrones = generatableDrones.filter(drone =>
                drone.name.toLowerCase().includes(searchLower)
            );
        }
    }

    clearFilters(): void {
        this.searchQuery = '';
        this.filterOrganization = '';
        this.filterDrone = '';
        this.filterStatus = 'all';
        this.filterStartDate = '';
        this.filterEndDate = '';
        this.onFilterChange();
    }


    // ======================
    // POLLING
    // ======================

    private startPolling(): void {
        if (this.pollingInterval) return;

        this.pollingInterval = setInterval(async () => {
            try {
                await this.fetchReports();
            } catch (error) {
                console.error('Polling fetch reports error:', error);
            }
        }, this.POLLING_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    private checkAndUpdatePolling(): void {
        const isGenerating = this.reports.some(report => report.status === 'generating');
        if (isGenerating && !this.pollingInterval) {
            this.startPolling();
        } else if (!isGenerating && this.pollingInterval) {
            this.stopPolling();
        }
    }

    // ============================================
    // PAGINATION
    // ============================================

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadReports();
        }
    }

    get paginationRange(): number[] {
        const range: number[] = [];
        const maxButtons = 5;
        let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(this.totalPages, start + maxButtons - 1);

        if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
        }

        for (let i = start; i <= end; i++) {
        range.push(i);
        }

        return range;
    }

    // ======================
    // LOAD
    // ======================

    async loadOrganizations(): Promise<void> {
        try {
            this.organizations = await this.droneReportService.listOrganizations();
        } catch (error) {
            console.error('Failed to load organizations:', error);
        }
    }

    // ======================
    // DRONE LOADING
    // ======================

    private async loadAdnDrones(): Promise<void> {
        try {
            const adnDrones = await firstValueFrom(this.droneService.getAllDrones());
            const mappedDrones: ReportDrone[] = adnDrones.map(d => ({
                id: d.id,
                name: d.name ?? '',
                droneSource: 'adn',
                organizationId: d.assignedOrganizationId ?? null,
                canGenerateReport: !!d.assignedOrganizationId,
            }));
            this.drones = [...mappedDrones, ...this.drones];
            this.applyDroneFilter();
        } catch (error) {
            console.error('Failed to load ADN drones:', error);
        }
    }

    private loadOrgDrones(orgId: string): Promise<void> {
        const existing = this.orgDroneRequests.get(orgId);
        if (existing) return existing;

        const request = (async () => {
            try {
                const drones = await firstValueFrom(this.droneService.getOrganizationDronesFlat(orgId));
                const ownedDrones: ReportDrone[] = drones
                    .filter(drone => drone.source === 'organization')
                    .map(drone => ({
                        id: drone.id,
                        name: drone.name,
                        droneSource: 'organization',
                        organizationId: orgId,
                        canGenerateReport: true,
                    }));

                this.drones = [...this.drones, ...ownedDrones];
                this.applyDroneFilter();
            } catch (error) {
                console.error(`Failed to load drones for organization ${orgId}:`, error);
                this.orgDroneRequests.delete(orgId);
            }
        })();

        this.orgDroneRequests.set(orgId, request);
        return request;
    }

    // ======================
    // REPORT LOADING
    // ======================

    async loadReports(): Promise<void> {
        this.loading = true;
        this.errorMessage = '';

        try {
            await this.fetchReports();
            // Keep the spinner until the visible rows' drone names are loaded
            await Promise.all([
                this.adnDronesReady,
                ...this.reports.map(r => this.loadReportOrgDrones(r)),
            ]);

        } catch (error: any) {
            console.error('Load reports error:', error);
            this.errorMessage = 'Failed to load reports';
        } finally {
            this.loading = false;
        }
    }

    private async fetchReports(): Promise<void> {
        const filters: DroneReportFilters = {
            page: this.currentPage,
            pageSize: this.pageSize,
        };

        if (this.filterStatus !== 'all') {
            filters.status = this.filterStatus;
        }
        if (this.filterOrganization) {
            filters.organizationId = this.filterOrganization;
        }
        if (this.filterDrone) {
            const drone = this.drones.find(d => d.id === this.filterDrone);
            if (drone) {
                filters.droneId = this.filterDrone;
                filters.droneSource = drone.droneSource;
            }
        }
        if (this.filterStartDate) {
            filters.startDate = this.filterStartDate;
        }
        if (this.filterEndDate) {
            filters.endDate = this.filterEndDate;
        }
        if (this.searchQuery.trim()) {
            filters.search = this.searchQuery.trim();
        }

        const page = await this.droneReportService.listReports(filters);

        this.reports = page.items;
        this.totalReports = page.paging.totalItems;
        this.totalPages = Math.ceil(this.totalReports / this.pageSize);

        this.checkAndUpdatePolling();
        this.reports.forEach(r => this.loadReportOrgDrones(r));
    }

    /** Loads the drones of a report's organization, for its drone name. */
    private loadReportOrgDrones(report: DroneReport): Promise<void> {
        return report.organizationId ? this.loadOrgDrones(report.organizationId) : Promise.resolve();
    }

    // ======================
    // REPORT GENERATION
    // ======================

    async executeReportGeneration(): Promise<void> {
        if (!this.canSubmitReport() || !this.selectedDrone) return;

        this.reportGenerating = true;
        this.errorMessage = '';

        try {
            await this.droneReportService.generateReport({
                droneId: this.selectedDrone.id,
                droneSource: this.selectedDrone.droneSource,
                scope: 'full_history',
                startDate: this.reportStartDate,
                endDate: this.reportEndDate
            });

            this.generateSectionExpanded = false;
            this.successMessage = 'Successfully queued report for generation';
            this.cancelDroneReportConfirmation();
            await this.loadReports();
            this.clearSuccessMessage();

        } catch (error: any) {
            console.error('Generate report error:', error);
            this.errorMessage = 'Failed to generate report';
        } finally {
            this.reportGenerating = false;
        }
    }


    // ======================
    // VIEW 
    // ======================

    openReportUrl(): void {
        const url = this.selectedReport?.downloadUrl || this.selectedReport?.reportUrl;
        if (url) {
            openExternal(url);
        }
    }

    closeDetailModal(): void {
        this.selectedReport = null;
        this.closeModal('detailModal');
    }

    async viewReport(report: DroneReport): Promise<void> {
        try {
          this.selectedReport = await this.droneReportService.getReport(report.id);
          this.openModal('detailModal');
        } catch (error: any) {
          console.error('Failed to load report details:', error);
          this.errorMessage = 'Failed to load report details';
        }
    }

    // ======================
    // DELETE
    // ======================

    confirmDelete(report: DroneReport): void {
        this.reportToDelete = report;
        this.openModal('deleteModal');
    }

    cancelDelete(): void {
        this.reportToDelete = null;
        this.closeModal('deleteModal');
    }

    canDelete(report: DroneReport): boolean {
        return report.status !== 'generating';
    }

    async executeDelete(): Promise<void> {
        if (!this.reportToDelete) return;

        this.actionLoading = true;
        this.errorMessage = '';

        try {
            await this.droneReportService.deleteReport(this.reportToDelete.id);
            this.successMessage = 'Report deleted successfully';
            this.loadReports();
            this.clearSuccessMessage();
        } catch (error: any) {
            this.successMessage = '';
            console.error('Failed to delete report:', error);
            this.errorMessage = 'Failed to delete report';
        }
        finally {
            this.actionLoading = false;
            this.cancelDelete();
        }
    }

    // ======================
    // REGENERATE
    // ======================

    confirmRegenerate(report: DroneReport): void {
        this.reportToRegenerate = report;
        this.openModal('regenerateModal');
    }
    
    cancelRegenerate(): void {
        this.reportToRegenerate = null;
        this.closeModal('regenerateModal');
    }

    async executeRegenerate(): Promise<void> {
        if (!this.reportToRegenerate) return;

        this.actionLoading = true;
        this.errorMessage = '';

        try {
            await this.droneReportService.regenerateReport(this.reportToRegenerate.id);
            this.loadReports();

        } catch (error: any) {
            console.error('Failed to regenerate report:', error);
            this.errorMessage = 'Failed to regenerate report';
        } finally {
            this.actionLoading = false;
            this.cancelRegenerate();
        }
    }

    // ======================
    // RETRY
    // ======================

    canRetry(report: DroneReport): boolean {
        return report.status === 'failed';
    }

    async retryReport(report: DroneReport): Promise<void> {
        if (!this.canRetry(report)) return;

        this.actionLoading = true;
        this.errorMessage = '';

        try {
            await this.droneReportService.retryReport(report.id);
            await this.loadReports();

        } catch (error: any) {
            console.error('Failed to retry report:', error);
            this.errorMessage = 'Failed to retry report';
        } finally {
            this.actionLoading = false;
        }
    }
    
    

    // ======================
    // HELPER METHODS
    // ======================

    formatDate(value: Date | string | null | undefined): string {
        return formatReportDate(value instanceof Date ? value.toISOString() : value);
    }

    formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
        return formatReportDateRange(startDate, endDate);
    }

    formatTimestamp(timestamp: number | undefined): string {
        return formatReportTimestamp(timestamp);
    }

    getOrganizationName(organizationId: string | null | undefined): string {
       return organizationId ? getOrganizationName(organizationId, this.organizations) : '—';
    }

    getDroneName(droneId: string | null | undefined): string {
        const drone = this.drones.find(d => d.id === droneId);
        return drone?.name || droneId || '—';
    }

    getStatusIcon(status: DroneReportStatus | null | undefined): string {
        return status ? getReportStatusIcon(status) : 'bi-circle';
    }

    getStatusLabel(status: DroneReportStatus | null | undefined): string {
        return status ? getReportStatusLabel(status) : '—';
    }

    getStatusClass(status: DroneReportStatus | null | undefined): string {
        return status ? getReportStatusClass(status) : 'bg-secondary';
    }

    private openModal(modalId: string): void {
        const modalElement = document.getElementById(modalId);
        if (modalElement) {
        const modal = new (window as any).bootstrap.Modal(modalElement);
        modal.show();
        }
    }

    private closeModal(modalId: string): void {
        const modalElement = document.getElementById(modalId);
        if (modalElement) {
        const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
        if (modal) {
            modal.hide();
        }
        }
    }

    private clearSuccessMessage(): void {
        if (this.successMessageTimeout){
            clearTimeout(this.successMessageTimeout);
        }

        this.successMessageTimeout = setTimeout(() => {
            this.successMessage = '';
            this.successMessageTimeout = null;
        }, 3000);
    }

    // ======================
    // PROGRESS METHODS
    // ======================

    private updateReport(reportId: string, updates: Partial<DroneReport>): void {
        this.reports = this.reports.map(r =>
            r.id === reportId ? withChanges(r, updates) : r
        );
    }

    private onReportProgress = (event: DroneReportProgressEvent): void => {
        this.updateReport(event.reportId, {
            generationProgress: event.progress,
            status: event.status
        });
    }

    private onReportCompletion = (event: DroneReportCompletionEvent): void => {
        this.updateReport(event.reportId, {
            status: event.status,
            recordCount: event.recordCount,
            fileSize: event.fileSize,
            generationProgress: 100
        });
        this.successMessage = `Report generated successfully.`;
        this.checkAndUpdatePolling();
        this.clearSuccessMessage();
    }

    private onReportFailure = (event: DroneReportFailedEvent): void => {
        this.updateReport(event.reportId, {
            status: event.status,
            generationProgress: 0,
            errorMessage: event.error
        });
        this.errorMessage = event.error;
        this.successMessage = '';
        this.checkAndUpdatePolling();
    }

    readonly Math = Math;
}